import React, { Suspense } from 'react'
import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd'
import { ChunkErrorBoundary } from './components/ChunkErrorBoundary'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import './scss/style.scss'
import { ThemeProvider, useTheme } from './features/theme'
import { useSession } from './features/session'
import { path } from './libs'
import {
  NotFoundPage,
  Dashboard,
  ClientsPage,
  ClientDetail,
  ClientEditDetail,
  VerificationPageV2,
  VerificationViewV2,
  VerificationFlowV2,
  VerificationRequestEditDetailV2,
  VerificationWorkReportPage,
  GatewayPage,
  GatewayEditDetail,
  GatewayCreateDetail,
  Login,
  TransactionPage,
} from './pages'
import { IdeasPage } from './pages/ideas/page'
import { CSpinner } from '@coreui/react-pro'
import { ClientAccountEdit } from './pages/clients/account'
import {
  DepositDetail,
  DepositEditDetail,
  DepositPage,
  WithdrawalDetail,
  WithdrawalPage,
  WithdrawalEditDetail,
  InternalTransferDetail,
  PaymentTrafficMonitoring,
} from './pages/billing'
import { ClientBonusAccountEdit } from './pages/clients/bonus'
import {
  DividendsPage,
  DividendEditDetail,
  DividendEventDetail,
  OrdersPage,
  OwnerWalletsPage,
} from './pages/nova'
import { InternalTransferList } from './pages/billing/internalTransfers/list/page'
import { UserDetail, UserEditDetail, UsersPage } from './pages/usersRoles/users'
import {
  CardVerificationPage,
  CardVerificationRequestEditDetail,
  CardVerificationView,
} from './pages/cardVerification'
import { PartnersList } from './pages/ib/partners/list/page'
import { PartnerDetail, PartnerEditDetail } from './pages/ib/partners'
import { KPIPage } from './pages/kpi'
import { KPIView } from './pages/kpi/details'
import { TransactionByDayPage } from './pages/kpi/transactions/page'
import { GiftsPage } from './pages/giftshop/gifts/page'
import { EditGift } from './pages/giftshop/gifts/edit'
import { CategoriesPage } from './pages/giftshop/giftsCategories/page'
import { GiftCategoryEditDetail } from './pages/giftshop/giftsCategories/edit'
import { GiftDetail } from './pages/giftshop/gifts/details'
import { CreateGift } from './pages/giftshop/gifts/create/page'
import { CreateGiftCategory } from './pages/giftshop/giftsCategories/create/page'
import { GiftOrdersPage } from './pages/giftshop/giftOrders/page'
import { GiftOrderDetail } from './pages/giftshop/giftOrders/details'
import { AttachClientToPartnerDetail } from './pages/ib/partners/attach'
import { BalanceOperationPage } from './pages/billing/balanceOperation/page'
import { CreateBalanceOperation } from './pages/billing/balanceOperation/create/page'
import { AIAssessment, StrategiesPage } from './pages/copytrade'
import { StrategyEdit } from './pages/copytrade'
import { StrategyDetail } from './pages/copytrade/strategies/details'
import { AchievementsPage } from './pages/copytrade/achievements/page'
import { CreateAchievement } from './pages/copytrade/achievements/create/page'
import { EditAchievement } from './pages/copytrade/achievements/edit/page'
import { DomainList } from './pages/forbiddenDomain/list/page'
import { DomainCreateDetail } from './pages/forbiddenDomain'
import { TradersPage } from './pages/anniversaryContest/traders/page'
import { AnniversaryContestPartnersPage } from './pages/anniversaryContest/partners/page'
import { ExchangersPage } from './pages/exchanger/page'
import { CreateExchanger } from './pages/exchanger/create/page'
import { EditExchanger } from './pages/exchanger/edit/page'
import { ExchangerTransfersPage } from './pages/billing/exchangerTransfer/page'
import { ExchangerTransferDetail } from './pages/billing/exchangerTransfer/details/page'
import {
  ChargebacksPage,
  ChargebackCreatePage,
  ChargebackEditPage,
} from './pages/billing/chargeback'
import {
  WalletsPage,
  RevisionPage,
  RevisionDetails,
  ReportDividends,
  ReportCommissions,
  ReportExDifference,
  DebtPage,
} from './pages/finSystem'
import { WalletDetails } from './pages/finSystem/wallets/details'
import { EditGiftOrder } from './pages/giftshop/giftOrders/edit'
import { CreateWithdrawalGroup } from './pages/withdrawalGroups/create/page'
import { WithdrawalGroupsPage } from './pages/withdrawalGroups/page'
import { WithdrawalGroupEditDetail } from './pages/withdrawalGroups/edit'
import { BudgetItemPage } from './pages/finSystem/budgetItem/page'
import { SectionPage } from './pages/finSystem/sections/page'
import { CreateBudgetItem } from './pages/finSystem/budgetItem/create/page'
import { CreateSection } from './pages/finSystem/sections/create/page'
import { EditBudgetItem } from './pages/finSystem/budgetItem/edit/page'
import { EditSection } from './pages/finSystem/sections/edit/page'
import { LandingsPage } from './pages/ib/adMaterials/landings'
import { CreateLanding } from './pages/ib/adMaterials/landings/create/page'
import { EditLanding } from './pages/ib/adMaterials/landings/edit/page'
import { OffersPage } from './pages/ib/adMaterials/offers'
import { SocialMediaPage } from './pages/ib/adMaterials/socialMedia'
import { CreateSocialMediaGroup } from './pages/ib/adMaterials/socialMedia/create/page'
import { EditSocialMedia } from './pages/ib/adMaterials/socialMedia/edit/page'
import { CreateOffer } from './pages/ib/adMaterials/offers/create/page'
import { WebsiteBannersPage } from './pages/ib/adMaterials/banners/page'
import { InProgressDetails } from './pages/finSystem/wallets/inProgressDetail/page'
import { BalanceSummaryPage } from './pages/finSystem/balanceSummary/page'
import { WalletGuardDashboardPage } from './pages/walletGuard/dashBoard/page'
import { WalletGuardAlertsPage } from './pages/walletGuard/alerts/page'
import { WalletGuardCheckPage } from './pages/walletGuard/check/page'
import { WalletGuardWalletsPage } from './pages/walletGuard/wallets/page'
import { WalletGuardWalletDetailsPage } from './pages/walletGuard/wallets/details/page'
import { WalletGuardWalletEditPage } from './pages/walletGuard/wallets/edit/page'
import { CreateWebBannerGroup } from './pages/ib/adMaterials/banners/create/page'
import { EditWebsiteBanner } from './pages/ib/adMaterials/banners/edit/page'
import { EditOffer } from './pages/ib/adMaterials/offers/edit/page'
import { DemoContestPage } from './pages/demoContest/page'
import { DemoContestDetail } from './pages/demoContest/details/page'
import { CreateDemoContest } from './pages/demoContest/create/page'
import { EditDemoContest } from './pages/demoContest/edit/page'
import { SellOffersPage } from './pages/nova/sell-offers/page'
import { SellPayoutsPage } from './pages/nova/sell-payouts/page'
import { HotBuyOrdersPage } from './pages/nova/hot-buy-orders/page'
import { Ramadan2025CompetitionsPage } from './pages/ramadan2025/competitions/page'
import { Ramadan2025ParticipantsPage } from './pages/ramadan2025/participants/page'
import { Ramadan2026CompetitionsPage } from './pages/ramadan2026/competitions/page'
import { Ramadan2026ParticipantsPage } from './pages/ramadan2026/participants/page'
import { CompetitionsDetails } from './pages/ramadan2025/competitions/details/page'
import { Ramadan2026CompetitionsDetails } from './pages/ramadan2026/competitions/details/page'
import { PromoCodesPage } from './pages/depositBonus/promoCodes/page'
import { CreatePromoCode } from './pages/depositBonus/promoCodes/create/page'
import { CountrySettingPage } from './pages/depositBonus/settings/page'
import { PromoCodesUsagePage } from './pages/depositBonus/promoCodeUsage/page'
import { EditPromoCode } from './pages/depositBonus/promoCodes/edit/page'
import { Guard } from './features/session/guard'
import { ServicesPage } from './pages/translations/services/page'
import { EditService } from './pages/translations/services/edit'
import { AllTransactionPage } from './pages/finSystem/allTransaction/page'
import { GoldenMay2025ParticipantsPage } from './pages/goldenMay2025/participants/page'
import { GoldenMay2025TasksPage } from './pages/goldenMay2025/tasks/page'
import { InstalmentsListPage } from './pages/nova/instalments/page'
import { GoldenMay2025WinnersPage } from './pages/goldenMay2025/winners/page'
import { CashBoosterPage } from './pages/ib/сashbooster/page'
import { GroupsPage } from './pages/usersRoles/groups/page'
import { PermissionsPage } from './pages/usersRoles/permissions/page'
import { RolesPage } from './pages/usersRoles/roles/page'
import { RoleDetailsPage } from './pages/usersRoles/roles/details/page'
import { CreateGroup } from './pages/usersRoles/groups/create/page'
import { EditGroup } from './pages/usersRoles/groups/edit/page'
import { CreateUser } from './pages/usersRoles/users/create/page'
import { EditPermission } from './pages/usersRoles/permissions/edit/page'
import { CreatePermission } from './pages/usersRoles/permissions/create/page'
import { TechPage } from './pages/tech'
import { DetailsService } from './pages/translations/services/details/page'
import { PropertyList } from './pages/nova/properties/list/page'
import { PropertyDetails } from './pages/nova/properties/details/page'
import { NewYear2026ParticipantsPage } from './pages/newYear2026/participants/page'
import { NewYear2026TasksPage } from './pages/newYear2026/tasks/page'
import { NewYear2026WinnersPage } from './pages/newYear2026/winners/page'
import { NewYear2026TicketsPage } from './pages/newYear2026/tickets/page'
import { Bonus150ParticipantsPage } from './pages/promo/bonus150/participants/page'
import { Bonus150SettingsPage } from './pages/promo/bonus150/settings/page'
import { Notification } from './features/notifications/notification'
import { KPIList } from './pages/financialDepartment/kpi/list/page'
import { QuarterlyKPIList } from './pages/financialDepartment/quarterlyKpi/page'
import { ProcessingTransactionsList } from './pages/financialDepartment/processingTransactions/list/page'
import { KPISettings } from './pages/financialDepartment/kpiSettings/list/page'
import { CardVerificationsList } from './pages/financialDepartment/cardVerification/list/page'
import {
  BudgetCategories,
  BudgetRequests,
  BudgetTeams,
  CreatePaymentRequest,
  CreateRequest,
  EditRequest,
} from 'src/pages/budget'
import { GoldenMay2026ParticipantsPage } from 'src/pages/goldenMay2026/participants/page'
import { GoldenMay2026TasksPage } from 'src/pages/goldenMay2026/tasks/page'
import { GoldenMay2026WinnersPage } from 'src/pages/goldenMay2026/winners/page'
import { NotificationLayoutGoldenMay2026 } from 'src/pages/goldenMay2026/NotificationLayout'
import { GoldenMay2026TicketsPage } from 'src/pages/goldenMay2026/tickets/page'
import {
  WorldCup2026ParticipantsPage,
  WorldCup2026PlayersPage,
  WorldCup2026TasksPage,
  WorldCup2026TicketsPage,
  WorldCup2026WinnersPage,
} from 'src/pages/worldCup2026'
import {
  CreateWebSitePage,
  EditWebSitePage,
  WebSitePageList,
} from 'src/pages/ib/adMaterials/webSitePages'
import { GUARD_RULES, PERMISSIONS } from 'src/features/session/constants'
import { PartnerContest2026Page } from 'src/pages/partnerContest2026'

// Retry dynamic import on chunk load failure (stale cache after deploy)
function lazyWithRetry(
  factory: () => Promise<{ default: React.ComponentType<any> }>,
) {
  return React.lazy(() =>
    factory().catch((error) => {
      // Only reload once to avoid infinite loop
      const key = 'chunk_reload'
      const hasReloaded = sessionStorage.getItem(key)
      if (!hasReloaded) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
        return new Promise(() => {}) // never resolves — page will reload
      }
      sessionStorage.removeItem(key)
      throw error
    }),
  )
}

// Containers
const DefaultLayout = lazyWithRetry(() => import('./layout/DefaultLayout'))
const AuthLayout = lazyWithRetry(() => import('./layout/AuthLayout'))

const router = createBrowserRouter([
  {
    path: path.root(),
    element: <Navigate to={path.dashboard()} />,
  },
  {
    element: <AuthLayout />,
    children: [
      {
        path: path.login.entry(),
        element: <Login />,
      },
    ],
  },
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.dashboard(),
        element: (
          <Guard permission={GUARD_RULES.LOGGED}>
            <Dashboard />
          </Guard>
        ),
      },
      {
        path: path.ideas.entry(),
        element: (
          <Guard permission={GUARD_RULES.LOGGED}>
            <IdeasPage />
          </Guard>
        ),
      },
    ],
  },
  //clients
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.clients.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CLIENTS}>
            <ClientsPage />
          </Guard>
        ),
      },
      {
        path: path.clients.details(':clientID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CLIENT_PROFILE_DETAIL}>
            <ClientDetail />
          </Guard>
        ),
      },
      {
        path: path.clients.edit(':clientID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_CLIENTS_DATA}>
            <ClientEditDetail />
          </Guard>
        ),
      },
      {
        path: path.clients.accountDetail(':clientID', ':accountID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_ACCOUNT}>
            <ClientAccountEdit />
          </Guard>
        ),
      },
      {
        path: path.clients.bonus111Detail(':clientID', ':accountID'),
        element: (
          <Guard permission={GUARD_RULES.LOGGED}>
            <ClientBonusAccountEdit />
          </Guard>
        ),
      },
    ],
  },
  //users
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.users.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_USERS}>
            <UsersPage />
          </Guard>
        ),
      },
      {
        path: path.users.details(':userID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_USERS}>
            <UserDetail />
          </Guard>
        ),
      },
      {
        path: path.users.create(),
        element: (
          <Guard permission={GUARD_RULES.EDIT_USERS}>
            <CreateUser />
          </Guard>
        ),
      },
      {
        path: path.users.edit(':userID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_USERS}>
            <UserEditDetail />
          </Guard>
        ),
      },
      {
        path: path.groups.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_USERS}>
            <GroupsPage />
          </Guard>
        ),
      },
      {
        path: path.groups.create(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <CreateGroup />
          </Guard>
        ),
      },
      {
        path: path.groups.edit(':groupID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <EditGroup />
          </Guard>
        ),
      },
      {
        path: path.permissions.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <PermissionsPage />
          </Guard>
        ),
      },
      {
        path: path.permissions.create(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <CreatePermission />
          </Guard>
        ),
      },
      {
        path: path.permissions.edit(':permissionID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <EditPermission />
          </Guard>
        ),
      },
      {
        path: path.roles.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <RolesPage />
          </Guard>
        ),
      },
      {
        path: path.roles.details(':roleID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_ROLE}>
            <RoleDetailsPage />
          </Guard>
        ),
      },
    ],
  },
  // Verifications
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.verificationsV2.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_VERIFICATION_LIST}>
            <VerificationPageV2 />
          </Guard>
        ),
      },
      {
        path: path.verificationsV2.flow(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_VERIFICATION_LIST}>
            <VerificationFlowV2 />
          </Guard>
        ),
      },
      {
        path: path.verificationsV2.details(':verificationsID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_VERIFICATION}>
            <VerificationViewV2 />
          </Guard>
        ),
      },
      {
        path: path.verificationsV2.edit(':verificationID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_VERIFICATION}>
            <VerificationRequestEditDetailV2 />
          </Guard>
        ),
      },
      {
        path: path.verificationsV2.workReport(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_VERIFICATION_WORK_REPORT}>
            <VerificationWorkReportPage />
          </Guard>
        ),
      },
    ],
  },
  //KPI
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.kpi.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_KPI}>
            <KPIPage />
          </Guard>
        ),
      },
      {
        path: path.kpi.details(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_KPI}>
            <KPIView />
          </Guard>
        ),
      },
      {
        path: path.kpi.transactions(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_KPI}>
            <TransactionByDayPage />
          </Guard>
        ),
      },
    ],
  },
  //Forbidden domain
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.forbiddenDomain.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FORBIDDEN_DOMAINS_LIST}>
            <DomainList />
          </Guard>
        ),
      },
      {
        path: path.forbiddenDomain.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_FORBIDDEN_DOMAINS}>
            <DomainCreateDetail />
          </Guard>
        ),
      },
    ],
  },
  //card verifications
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.cardVerifications.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CARD_VERIFICATION_LIST}>
            <CardVerificationPage />
          </Guard>
        ),
      },
      {
        path: path.cardVerifications.details(':cardID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CARD_VERIFICATION_LIST}>
            <CardVerificationView />
          </Guard>
        ),
      },
      {
        path: path.cardVerifications.edit(':cardID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_CARD_VERIFICATION_CARD}>
            <CardVerificationRequestEditDetail />
          </Guard>
        ),
      },
    ],
  },
  //IB
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.ib.partners.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_LIST_OF_PARTNERS}>
            <PartnersList />
          </Guard>
        ),
      },
      {
        path: path.ib.cashbooster.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_MONTH_RESULTS_CASHBOOSTER}>
            <CashBoosterPage />
          </Guard>
        ),
      },
      {
        path: path.ib.partners.details(':partnerID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_PARTNER_PROFILE}>
            <PartnerDetail />
          </Guard>
        ),
      },
      {
        path: path.ib.partners.edit(':partnerID'),
        element: (
          <Guard permission={GUARD_RULES.CHANGE_STATUS_OF_PARTNER}>
            <PartnerEditDetail />
          </Guard>
        ),
      },
      {
        path: path.ib.partners.attach(),
        element: (
          <Guard permission={GUARD_RULES.ATTACH_CLIENT_TO_PARTNER}>
            <AttachClientToPartnerDetail />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.website_pages.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_AD_MATERIALS}>
            <WebSitePageList />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.website_pages.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_AD_MATERIALS}>
            <CreateWebSitePage />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.website_pages.edit(':id'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_AD_MATERIALS}>
            <EditWebSitePage />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.landing.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_AD_MATERIALS}>
            <LandingsPage />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.landing.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_AD_MATERIALS}>
            <CreateLanding />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.landing.edit(':landingID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_AD_MATERIALS}>
            <EditLanding />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.offers.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_AD_MATERIALS}>
            <OffersPage />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.offers.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_AD_MATERIALS}>
            <CreateOffer />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.offers.edit(':offerID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_AD_MATERIALS}>
            <EditOffer />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.socialMedia.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_AD_MATERIALS}>
            <SocialMediaPage />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.socialMedia.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_AD_MATERIALS}>
            <CreateSocialMediaGroup />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.socialMedia.edit(':socialMediaID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_AD_MATERIALS}>
            <EditSocialMedia />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.banners.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_AD_MATERIALS}>
            <WebsiteBannersPage />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.banners.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_AD_MATERIALS}>
            <CreateWebBannerGroup />
          </Guard>
        ),
      },
      {
        path: path.ib.ad.banners.edit(':websiteBannerID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_AD_MATERIALS}>
            <EditWebsiteBanner />
          </Guard>
        ),
      },
    ],
  },
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.billing.gateway.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_PAYMENTS}>
            <GatewayPage />
          </Guard>
        ),
      },
      {
        path: path.billing.gateway.edit(':gatewayID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_PAYMENTS}>
            <GatewayEditDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.gateway.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_PAYMENTS}>
            <GatewayCreateDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.paymentGroup.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_PAYMENTS_WD_GROUP}>
            <WithdrawalGroupsPage />
          </Guard>
        ),
      },
      {
        path: path.billing.paymentGroup.edit(':groupID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_PAYMENTS_WD_GROUP}>
            <WithdrawalGroupEditDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.paymentGroup.create(),
        element: (
          <Guard permission={GUARD_RULES.EDIT_PAYMENTS_WD_GROUP}>
            <CreateWithdrawalGroup />
          </Guard>
        ),
      },
      {
        path: path.billing.transactions.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <TransactionPage />
          </Guard>
        ),
      },
      {
        path: path.billing.deposit.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <DepositPage />
          </Guard>
        ),
      },
      {
        path: path.billing.deposit.details(':depositID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <DepositDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.deposit.edit(':depositID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <DepositEditDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.withdrawal.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <WithdrawalPage />
          </Guard>
        ),
      },
      {
        path: path.billing.withdrawal.details(':withdrawalID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <WithdrawalDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.withdrawal.edit(':withdrawalID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <WithdrawalEditDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.internalTransfer.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <InternalTransferList />
          </Guard>
        ),
      },
      {
        path: path.billing.internalTransfer.details(':internalTransferID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <InternalTransferDetail />
          </Guard>
        ),
      },
      {
        path: path.billing.balanceOperation.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BALANCE_OPERATION}>
            <BalanceOperationPage />
          </Guard>
        ),
      },
      {
        path: path.billing.balanceOperation.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_BALANCE_OPERATION}>
            <CreateBalanceOperation />
          </Guard>
        ),
      },
      {
        path: path.billing.exchangerTransfer.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSACTIONS}>
            <ExchangerTransfersPage />
          </Guard>
        ),
      },
      {
        path: path.billing.exchangerTransfer.details(':exchangerTransferID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CARD_OF_TRANSACTION}>
            <ExchangerTransferDetail />
          </Guard>
        ),
      },
      {
        path: path.finSystem.debt.entry(),
        element: (
          <Guard
            permission={PERMISSIONS.VIEW_FINSYSTEM_DEBT_TRANSACTION}
            mode="permission"
          >
            <DebtPage />
          </Guard>
        ),
      },
      {
        path: path.billing.chargeback.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <ChargebacksPage />
          </Guard>
        ),
      },
      {
        path: path.billing.chargeback.create(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <ChargebackCreatePage />
          </Guard>
        ),
      },
      {
        path: path.billing.chargeback.edit(':chargebackID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <ChargebackEditPage />
          </Guard>
        ),
      },
      {
        path: path.billing.paymentTrafficMonitoring.entry(),
        element: (
          <Guard
            permission={PERMISSIONS.VIEW_PAYMENT_TRAFFIC_REPORT}
            mode="permission"
          >
            <PaymentTrafficMonitoring />
          </Guard>
        ),
      },
    ],
  },
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.nova.orders.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_ORDERS}>
            <OrdersPage />
          </Guard>
        ),
      },
      {
        path: path.nova.dividendEvents.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_DIVIDENDS}>
            <DividendsPage />
          </Guard>
        ),
      },
      {
        path: path.nova.dividendEvents.details(':dividendEventID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_DIVIDEND_EVENT}>
            <DividendEventDetail />
          </Guard>
        ),
      },
      {
        path: path.nova.dividendEvents.edit(':dividendEventID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_NOVA_DIVIDEND_EVENT}>
            <DividendEditDetail />
          </Guard>
        ),
      },
      {
        path: path.nova.ownerWallets.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_OWNER_WALLETS}>
            <OwnerWalletsPage />
          </Guard>
        ),
      },
      {
        path: path.nova.sellOffers.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_SELL_OFFERS}>
            <SellOffersPage />
          </Guard>
        ),
      },
      {
        path: path.nova.sellPayouts.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_SELL_PAYOUTS}>
            <SellPayoutsPage />
          </Guard>
        ),
      },
      {
        path: path.nova.hotBuyOrders.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_HOT_BUY_ORDERS}>
            <HotBuyOrdersPage />
          </Guard>
        ),
      },
      {
        path: path.nova.instalments.entry(null),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_INSTALMENTS}>
            <InstalmentsListPage />
          </Guard>
        ),
      },
      {
        path: path.nova.properties.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_LIST_OF_PROPERTIES}>
            <PropertyList />
          </Guard>
        ),
      },
      {
        path: path.nova.properties.detail(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_NOVA_CARD_OF_PROPERTIES}>
            <PropertyDetails />
          </Guard>
        ),
      },
    ],
  },
  //giftshop
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.giftShop.gifts.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_GIFTS}>
            <GiftsPage />
          </Guard>
        ),
      },
      {
        path: path.giftShop.gifts.edit(':giftID'),
        element: (
          <Guard permission={GUARD_RULES.CREATE_GIFT}>
            <EditGift />
          </Guard>
        ),
      },
      {
        path: path.giftShop.gifts.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_GIFT}>
            <CreateGift />
          </Guard>
        ),
      },
      {
        path: path.giftShop.gifts.details(':giftID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_GIFTS}>
            <GiftDetail />
          </Guard>
        ),
      },
      {
        path: path.giftShop.category.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_GIFT_CATEGORIES}>
            <CategoriesPage />
          </Guard>
        ),
      },
      {
        path: path.giftShop.category.edit(':giftCategoryID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_GIFT_CATEGORY}>
            <GiftCategoryEditDetail />
          </Guard>
        ),
      },
      {
        path: path.giftShop.category.create(),
        element: (
          <Guard permission={GUARD_RULES.EDIT_GIFT_CATEGORY}>
            <CreateGiftCategory />
          </Guard>
        ),
      },
      {
        path: path.giftShop.giftOrders.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_LIST_OF_GIFT_ORDERS}>
            <GiftOrdersPage />
          </Guard>
        ),
      },
      {
        path: path.giftShop.giftOrders.details(':giftOrderID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_GIFT_ORDER}>
            <GiftOrderDetail />
          </Guard>
        ),
      },
      {
        path: path.giftShop.giftOrders.edit(':giftOrderID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_GIFT_ORDER}>
            <EditGiftOrder />
          </Guard>
        ),
      },
    ],
  },
  //giftshop end

  //copytrade
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.copytrade.strategies.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_LIST_OF_STRATEGIES}>
            <StrategiesPage />
          </Guard>
        ),
      },
      {
        path: path.copytrade.strategies.edit(':strategyID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_STRATEGY}>
            <StrategyEdit />
          </Guard>
        ),
      },
      {
        path: path.copytrade.strategies.details(':strategyID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_STRATEGY}>
            <StrategyDetail />
          </Guard>
        ),
      },
      {
        path: path.copytrade.achievements.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_STRATEGY}>
            <AchievementsPage />
          </Guard>
        ),
      },
      {
        path: path.copytrade.achievements.edit(':achievementID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_STRATEGY}>
            <EditAchievement />
          </Guard>
        ),
      },
      {
        path: path.copytrade.achievements.create(),
        element: (
          <Guard permission={GUARD_RULES.EDIT_STRATEGY}>
            <CreateAchievement />
          </Guard>
        ),
      },
      {
        path: path.copytrade.assessment.entry(),
        element: (
          <Guard permission={GUARD_RULES.EDIT_AI_ASSESSMENT}>
            <AIAssessment />
          </Guard>
        ),
      },
    ],
  },
  //copytrade end

  // anniversaryContest
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.anniversaryContest.traders.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <TradersPage />
          </Guard>
        ),
      },
      {
        path: path.promo.anniversaryContest.partners.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <AnniversaryContestPartnersPage />
          </Guard>
        ),
      },
    ],
  },
  // anniversaryContest end

  // demoContestClient
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.demoContest.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_LIST_OF_DEMOCONTEST}>
            <DemoContestPage />
          </Guard>
        ),
      },
      {
        path: path.promo.demoContest.details(':demoContestID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_DEMOCONTEST}>
            <DemoContestDetail />
          </Guard>
        ),
      },
      {
        path: path.promo.demoContest.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_DEMOCONTEST}>
            <CreateDemoContest />
          </Guard>
        ),
      },
      {
        path: path.promo.demoContest.edit(':demoContestID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_DEMOCONTEST}>
            <EditDemoContest />
          </Guard>
        ),
      },
    ],
  },
  // demoContestClient end

  // ramadan2025 start

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.ramadan2025.competitions.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <Ramadan2025CompetitionsPage />
          </Guard>
        ),
      },
      {
        path: path.promo.ramadan2025.competitions.details(':competitionID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <CompetitionsDetails />
          </Guard>
        ),
      },
      {
        path: path.promo.ramadan2025.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <Ramadan2025ParticipantsPage />
          </Guard>
        ),
      },
    ],
  },

  // ramadan2025 end

  // ramadan2026 start

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.ramadan2026.competitions.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <Ramadan2026CompetitionsPage />
          </Guard>
        ),
      },
      {
        path: path.promo.ramadan2026.competitions.details(':competitionID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <Ramadan2026CompetitionsDetails />
          </Guard>
        ),
      },
      {
        path: path.promo.ramadan2026.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <Ramadan2026ParticipantsPage />
          </Guard>
        ),
      },
    ],
  },

  // ramadan2026 end

  // goldenMay2025 start

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.goldenMay2025.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <GoldenMay2025ParticipantsPage />
          </Guard>
        ),
      },
      {
        path: path.promo.goldenMay2025.tasks.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <GoldenMay2025TasksPage />
          </Guard>
        ),
      },
      {
        path: path.promo.goldenMay2025.winners.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CONTEST}>
            <GoldenMay2025WinnersPage />
          </Guard>
        ),
      },
    ],
  },

  // goldenMay2025 end

  // goldenMay2026 start

  {
    element: <DefaultLayout />,
    children: [
      {
        element: <NotificationLayoutGoldenMay2026 />,
        children: [
          {
            path: path.promo.goldenMay2026.participants.entry(),
            element: (
              <Guard permission={GUARD_RULES.GOLDEN_MAY_2026_VIEW}>
                <GoldenMay2026ParticipantsPage />
              </Guard>
            ),
          },
          {
            path: path.promo.goldenMay2026.tasks.entry(),
            element: (
              <Guard permission={GUARD_RULES.GOLDEN_MAY_2026_VIEW}>
                <GoldenMay2026TasksPage />
              </Guard>
            ),
          },
          {
            path: path.promo.goldenMay2026.winners.entry(),
            element: (
              <Guard permission={GUARD_RULES.GOLDEN_MAY_2026_VIEW}>
                <GoldenMay2026WinnersPage />
              </Guard>
            ),
          },
          {
            path: path.promo.goldenMay2026.tickets.entry(),
            element: (
              <Guard permission={GUARD_RULES.VIEW_RESERVED_TICKETS_GM26}>
                <GoldenMay2026TicketsPage />
              </Guard>
            ),
          },
        ],
      },
    ],
  },

  // goldenMay2026 end

  // worldCup2026 start

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.worldCup2026.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.WORLD_CUP_2026_VIEW}>
            <WorldCup2026ParticipantsPage />
          </Guard>
        ),
      },
      {
        path: path.promo.worldCup2026.players.entry(),
        element: (
          <Guard permission={GUARD_RULES.WORLD_CUP_2026_VIEW}>
            <WorldCup2026PlayersPage />
          </Guard>
        ),
      },
      {
        path: path.promo.worldCup2026.tasks.entry(),
        element: (
          <Guard permission={GUARD_RULES.WORLD_CUP_2026_VIEW}>
            <WorldCup2026TasksPage />
          </Guard>
        ),
      },
      {
        path: path.promo.worldCup2026.winners.entry(),
        element: (
          <Guard permission={GUARD_RULES.WORLD_CUP_2026_VIEW}>
            <WorldCup2026WinnersPage />
          </Guard>
        ),
      },
      {
        path: path.promo.worldCup2026.tickets.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_RESERVED_TICKETS_WC26}>
            <WorldCup2026TicketsPage />
          </Guard>
        ),
      },
    ],
  },

  // worldCup2026 end

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.partnerContest2026.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.PARTNER_CONTEST_2026_VIEW}>
            <PartnerContest2026Page />
          </Guard>
        ),
      },
    ],
  },

  // newYear2026 start

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.newYear2026.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.NEW_YEAR_2026_VIEW}>
            <NewYear2026ParticipantsPage />
          </Guard>
        ),
      },
      {
        path: path.promo.newYear2026.tasks.entry(),
        element: (
          <Guard permission={GUARD_RULES.NEW_YEAR_2026_VIEW}>
            <NewYear2026TasksPage />
          </Guard>
        ),
      },
      {
        path: path.promo.newYear2026.winners.entry(),
        element: (
          <Guard permission={GUARD_RULES.NEW_YEAR_2026_VIEW}>
            <NewYear2026WinnersPage />
          </Guard>
        ),
      },
      {
        path: path.promo.newYear2026.tickets.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_RESERVED_TICKETS_NY26}>
            <NewYear2026TicketsPage />
          </Guard>
        ),
      },
    ],
  },

  // newYear2026 end

  // bonus150 start
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.promo.bonus150.participants.entry(),
        element: (
          <Guard permission={GUARD_RULES.NEW_YEAR_2026_VIEW}>
            <Bonus150ParticipantsPage />
          </Guard>
        ),
      },
      {
        path: path.promo.bonus150.settings.entry(),
        element: (
          <Guard permission={GUARD_RULES.NEW_YEAR_2026_VIEW}>
            <Bonus150SettingsPage />
          </Guard>
        ),
      },
    ],
  },
  // bonus150 end

  //exchangers
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.exchangers.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_EXCHANGER_LIST}>
            <ExchangersPage />
          </Guard>
        ),
      },
      {
        path: path.exchangers.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_EXCHANGER_WALLET}>
            <CreateExchanger />
          </Guard>
        ),
      },
      {
        path: path.exchangers.edit(':exchangerID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_EXCHANGER_WALLET}>
            <EditExchanger />
          </Guard>
        ),
      },
    ],
  },
  // Fin System
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.finSystem.wallets.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <WalletsPage />
          </Guard>
        ),
      },
      {
        path: path.finSystem.allTransactions.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <AllTransactionPage />
          </Guard>
        ),
      },
      {
        path: path.finSystem.wallets.details(':walletID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <WalletDetails />
          </Guard>
        ),
      },
      {
        path: path.finSystem.wallets.inProgressDetails(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_WALLETS}>
            <InProgressDetails />
          </Guard>
        ),
      },
      {
        path: path.finSystem.revision.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_REVISIONS}>
            <RevisionPage />
          </Guard>
        ),
      },
      {
        path: path.finSystem.budgetItem.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_ITEMS}>
            <BudgetItemPage />
          </Guard>
        ),
      },
      {
        path: path.finSystem.budgetItem.create(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_ITEMS}>
            <CreateBudgetItem />
          </Guard>
        ),
      },
      {
        path: path.finSystem.budgetItem.edit(':budgetItemID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_BUDGET_ITEMS}>
            <EditBudgetItem />
          </Guard>
        ),
      },
      {
        path: path.finSystem.sections.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_SECTIONS}>
            <SectionPage />
          </Guard>
        ),
      },
      {
        path: path.finSystem.sections.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_BUDGET_SECTIONS}>
            <CreateSection />
          </Guard>
        ),
      },
      {
        path: path.finSystem.sections.edit(':sectionID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_BUDGET_SECTIONS}>
            <EditSection />
          </Guard>
        ),
      },
      {
        path: path.finSystem.revision.details(':walletID', ':currency'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_REVISIONS}>
            <RevisionDetails />
          </Guard>
        ),
      },
      {
        path: path.finSystem.balanceSummary.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_STATEMENT}>
            <BalanceSummaryPage />
          </Guard>
        ),
      },
    ],
  },

  //KPI Financial Department
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.finDepartment.kpi.entry(),
        element: (
          <Guard
            permission={[
              GUARD_RULES.VIEW_KPI_FINDEP,
              GUARD_RULES.VIEW_KPI_FINDEP_OWN,
            ]}
          >
            <KPIList />
          </Guard>
        ),
      },
      {
        path: path.finDepartment.quarterlyKpi.entry(),
        element: (
          <Guard permission={[GUARD_RULES.VIEW_KPI_FINDEP, GUARD_RULES.VIEW_KPI_FINDEP_OWN]}>
            <QuarterlyKPIList />
          </Guard>
        ),
      },
      {
        path: path.finDepartment.processingTransactions.entry(),
        element: (
          <Guard
            permission={[
              GUARD_RULES.VIEW_KPI_FINDEP,
              GUARD_RULES.VIEW_KPI_FINDEP_OWN,
            ]}
          >
            <ProcessingTransactionsList />
          </Guard>
        ),
      },
      {
        path: path.finDepartment.settings.entry(),
        element: (
          <Guard
            permission={[
              GUARD_RULES.VIEW_KPI_FINDEP,
              GUARD_RULES.SETTING_KPI_FINDEP,
            ]}
          >
            <KPISettings />
          </Guard>
        ),
      },
      {
        path: path.finDepartment.cardVerification.entry(),
        element: (
          <Guard
            permission={[
              GUARD_RULES.VIEW_KPI_FINDEP,
              GUARD_RULES.VIEW_KPI_FINDEP_OWN,
            ]}
          >
            <CardVerificationsList />
          </Guard>
        ),
      },
    ],
  },

  // Deposit Bonus
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.depositBonus.promoCodes.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_LIST_OF_PROMOCODES}>
            <PromoCodesPage />
          </Guard>
        ),
      },
      {
        path: path.depositBonus.promoCodes.create(),
        element: (
          <Guard permission={GUARD_RULES.CREATE_PROMOCODE}>
            <CreatePromoCode />
          </Guard>
        ),
      },
      {
        path: path.depositBonus.promoCodes.edit(':promoCodeID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_PROMOCODE}>
            <EditPromoCode />
          </Guard>
        ),
      },
      {
        path: path.depositBonus.promoCodeUsage.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_PROMOCODES_USAGE}>
            <PromoCodesUsagePage />
          </Guard>
        ),
      },
      {
        path: path.depositBonus.settings.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_PROMOCODES_SETTINGS}>
            <CountrySettingPage />
          </Guard>
        ),
      },
    ],
  },
  // Deposit Bonus end

  // Translations start

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.translations.services.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_LIST_OF_TRANSLATIONS}>
            <ServicesPage />
          </Guard>
        ),
      },
      {
        path: path.translations.services.edit(':serviceName', ':lang'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_TRANSLATIONS}>
            <EditService />
          </Guard>
        ),
      },
      {
        path: path.translations.services.details(':serviceName'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_TRANSLATIONS}>
            <DetailsService />
          </Guard>
        ),
      },
    ],
  },

  // Translations end
  // Budget start &&

  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.budget.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <BudgetTeams />
          </Guard>
        ),
      },
      {
        path: path.budget.roles.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <BudgetTeams />
          </Guard>
        ),
      },
      {
        path: path.budget.categories.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <BudgetCategories />
          </Guard>
        ),
      },
      {
        path: path.budget.budgets.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <BudgetRequests />
          </Guard>
        ),
      },
      {
        path: path.budget.budgets.create(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <CreateRequest />
          </Guard>
        ),
      },
      {
        path: path.budget.budgets.edit(':id'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <EditRequest />
          </Guard>
        ),
      },
      {
        path: path.budget.budgets.createPaymentRequest(':id'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <CreatePaymentRequest />
          </Guard>
        ),
      },
      {
        path: path.budget.payments.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_BUDGET_DEPARTMENT}>
            <div>Payments</div>
          </Guard>
        ),
      },
    ],
  },

  // Budgets end
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.report.dividends.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_STATEMENT}>
            <ReportDividends />
          </Guard>
        ),
      },
    ],
  },
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.report.commissions.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_STATEMENT}>
            <ReportCommissions />
          </Guard>
        ),
      },
    ],
  },
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.report.exDifference.entry(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_FIN_STATEMENT}>
            <ReportExDifference />
          </Guard>
        ),
      },
    ],
  },

  // Technical start

  {
    path: path.technical(),
    element: (
      <Guard permission={GUARD_RULES.TECH_PAGE_VIEW}>
        <TechPage />
      </Guard>
    ),
  },

  // Technical end

  //walletGuard start
  {
    element: <DefaultLayout />,
    children: [
      {
        path: path.walletGuard.dashboard(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CRYPTO_WALLETS} mode="permission">
            <WalletGuardDashboardPage />
          </Guard>
        ),
      },
      {
        path: path.walletGuard.alerts(),
        element: (
          <Guard
            permission={GUARD_RULES.MANAGE_WALLET_ALERTS}
            mode="permission"
          >
            <WalletGuardAlertsPage />
          </Guard>
        ),
      },
      {
        path: path.walletGuard.check(),
        element: (
          <Guard
            permission={[
              GUARD_RULES.CHECK_CRYPTO_WALLET,
              GUARD_RULES.CHECK_CRYPTO_TRANSACTION,
            ]}
            mode="permission"
          >
            <WalletGuardCheckPage />
          </Guard>
        ),
      },
      {
        path: path.walletGuard.wallets(),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CRYPTO_WALLETS} mode="permission">
            <WalletGuardWalletsPage />
          </Guard>
        ),
      },
      {
        path: path.walletGuard.walletDetails(':walletID'),
        element: (
          <Guard permission={GUARD_RULES.VIEW_CRYPTO_WALLET} mode="permission">
            <WalletGuardWalletDetailsPage />
          </Guard>
        ),
      },
      {
        path: path.walletGuard.walletEdit(':walletID'),
        element: (
          <Guard permission={GUARD_RULES.EDIT_CRYPTO_WALLET} mode="permission">
            <WalletGuardWalletEditPage />
          </Guard>
        ),
      },
    ],
  },
  //walletGuard end

  {
    path: '*',
    element: <NotFoundPage />,
  },
])
const ThemedApp = () => {
  const { isDark } = useTheme()

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#3b82f6',
          borderRadius: 6,
        },
      }}
    >
      <AntdApp>
        <ChunkErrorBoundary>
          <Notification>
            <Suspense fallback={<CSpinner color="primary" />}>
              <RouterProvider router={router} />
            </Suspense>
          </Notification>
        </ChunkErrorBoundary>
      </AntdApp>
    </ConfigProvider>
  )
}

export const App = () => {
  useSession()

  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  )
}

export default App
